// Translation slice for Zustand store
import { translationApi } from "@/services/translationApi";
import {
  mergeWithFallback,
  getFallbackTranslations,
  resolveTranslationValue,
} from "@/lib/translationUtils";

export const createTranslationSlice = (set, get) => ({
  // Translation state
  translations: {},
  currentLanguage: "en",
  availableLanguages: ["en", "mk"],
  isLoadingTranslations: false,
  translationError: null,

  // Actions
  setCurrentLanguage: async (language) => {
    const currentLang = get().currentLanguage;

    // Don't reload if same language
    if (currentLang === language) {
      return;
    }

    set({
      currentLanguage: language,
      isLoadingTranslations: true,
      translationError: null,
    });

    try {
      // Load translations for the new language
      await get().loadTranslations(language);

      // Store language preference in localStorage
      if (typeof window !== "undefined") {
        localStorage.setItem("preferred_language", language);
      }

      // Save language preference to server
      try {
        await translationApi.setUserLanguagePreference(language);
      } catch (apiError) {
        // Don't fail the language change if API call fails
        console.warn("Failed to save language preference to server:", apiError);
      }
    } catch (error) {
      console.error("Error changing language:", error);
      set({
        translationError: error.message,
        isLoadingTranslations: false,
      });
    }
  },
  loadTranslations: async (language = null, force = false) => {
    const lang = language || get().currentLanguage;

    set({
      isLoadingTranslations: true,
      translationError: null,
    });

    try {
      const serverTranslations = await translationApi.loadTranslations(
        lang,
        force
      );
      const mergedTranslations = mergeWithFallback(serverTranslations, lang);

      set((state) => ({
        translations: {
          ...state.translations,
          [lang]: mergedTranslations,
        },
        isLoadingTranslations: false,
        translationError: null,
      }));

      return mergedTranslations;
    } catch (error) {
      console.error("Error loading translations:", error);

      // Use fallback translations
      const fallbackTranslations = getFallbackTranslations(lang);

      set((state) => ({
        translations: {
          ...state.translations,
          [lang]: fallbackTranslations,
        },
        isLoadingTranslations: false,
        translationError: `Failed to load translations: ${error.message}`,
      }));

      return fallbackTranslations;
    }
  },

  loadAvailableLanguages: async () => {
    try {
      const languages = await translationApi.getAvailableLanguages();
      set({ availableLanguages: languages });
      return languages;
    } catch (error) {
      console.error("Error loading available languages:", error);
      // Keep default languages
      return get().availableLanguages;
    }
  },

  preloadTranslations: async (languages = null) => {
    const langs = languages || get().availableLanguages;

    set({
      isLoadingTranslations: true,
      translationError: null,
    });

    try {
      const allTranslations = await translationApi.preloadTranslations(langs);

      set((state) => ({
        translations: {
          ...state.translations,
          ...allTranslations,
        },
        isLoadingTranslations: false,
        translationError: null,
      }));

      return allTranslations;
    } catch (error) {
      console.error("Error preloading translations:", error);
      set({
        translationError: error.message,
        isLoadingTranslations: false,
      });
      throw error;
    }
  },

  refreshTranslations: async (language = null) => {
    const lang = language || get().currentLanguage;

    // Clear cache for the language
    translationApi.clearCache(lang);

    // Reload translations
    return get().loadTranslations(lang, true);
  },

  clearTranslationCache: () => {
    translationApi.clearCache();
    set({
      translations: {},
      translationError: null,
    });
  },

  // Initialize translation system
  initializeTranslations: async () => {
    try {
      // Try to get preferred language from server first
      let preferredLang = "en";
      try {
        preferredLang = await translationApi.getUserLanguagePreference();
      } catch (serverError) {
        console.warn(
          "Failed to get language preference from server, falling back to localStorage:",
          serverError
        );
        // Fallback to localStorage if server call fails
        if (typeof window !== "undefined") {
          preferredLang = localStorage.getItem("preferred_language") || "en";
        }
      }

      // Load available languages
      await get().loadAvailableLanguages();

      // Set current language and load its translations
      await get().setCurrentLanguage(preferredLang);

      // Preload other languages in background
      const otherLanguages = get().availableLanguages.filter(
        (lang) => lang !== preferredLang
      );
      if (otherLanguages.length > 0) {
        // Don't await this - let it load in background
        get().preloadTranslations(otherLanguages).catch(console.error);
      }
    } catch (error) {
      console.error("Error initializing translations:", error);
      set({
        translationError: error.message,
        isLoadingTranslations: false,
      });
    }
  },

  // Get translation value by key path
  getTranslation: (keyPath, defaultValue = "") => {
    const { translations, currentLanguage } = get();
    const langTranslations = translations[currentLanguage];

    if (!langTranslations) {
      return defaultValue;
    }

    const value = resolveTranslationValue(langTranslations, keyPath);

    if (typeof value === "string") {
      return value;
    }

    return defaultValue;
  },

  // Check if translation exists
  hasTranslation: (keyPath) => {
    const { translations, currentLanguage } = get();
    const langTranslations = translations[currentLanguage];

    if (!langTranslations) {
      return false;
    }

    const value = resolveTranslationValue(langTranslations, keyPath);

    return typeof value === "string";
  },
});
